// Адресат упоминания и готовность к рассылке (005: FR-033, research, Р10). Не хранится —
// считается одной функцией для «Контроля», «Главной» и справочника, как статус пункта в 003.
import { z } from '../kernel/zod.ts';
import { RecordRefSchema, UnitRefSchema } from './record.ts';

/**
 * Как найден адресат: сам человек или основной куратор (организации или юрлица зоны); 006:
 * manager — руководитель человека «без почты», unit_email — общая почта подразделения.
 */
export const addresseeVias = ['self', 'curator', 'manager', 'unit_email'] as const;
export type AddresseeVia = (typeof addresseeVias)[number];
export const AddresseeViaSchema = z.enum(addresseeVias);

/**
 * Почему адресата нет (FR-033): нет годной почты, внешний адрес не подтверждён, запись в архиве,
 * у организации нет основного куратора, «не адресат», вид «неясно», упоминание ждёт решения
 * контролёра, у пункта без ответственного документ без зоны ответственности.
 */
export const addresseeReasons = [
  'no_email',
  'email_unconfirmed',
  'archived',
  'no_primary_curator',
  'not_addressee',
  'unclear',
  'unresolved',
  'no_zone',
  'no_manager',
  'unit_archived',
] as const;
export type AddresseeReason = (typeof addresseeReasons)[number];
export const AddresseeReasonSchema = z.enum(addresseeReasons);

export const addresseeReasonLabels = {
  no_email: 'нет почты',
  email_unconfirmed: 'адрес не подтверждён',
  archived: 'в архиве',
  no_primary_curator: 'нет основного куратора',
  not_addressee: 'не адресат',
  unclear: 'неясно, кто это',
  unresolved: 'ждёт решения',
  no_zone: 'у документа нет зоны ответственности',
  no_manager: 'руководитель не определён',
  unit_archived: 'подразделение в архиве',
} as const satisfies Record<AddresseeReason, string>;

/**
 * Адресат есть: кому письмо, копия (основной куратор внешней организации с признаком «получает
 * письма») и как найден. Адресата нет: причина и запись, у которой её исправить.
 */
export const AddresseeSchema = z.discriminatedUnion('ready', [
  z.object({
    ready: z.literal(true),
    /** Кому письмо; null — на общую почту подразделения unit (006, FR-011). */
    to: RecordRefSchema.nullable(),
    copy: RecordRefSchema.nullable(),
    via: AddresseeViaSchema,
    /** Подразделение, на общую почту которого уходит письмо (via = unit_email); иначе null. */
    unit: UnitRefSchema.nullable(),
  }),
  z.object({
    ready: z.literal(false),
    reason: AddresseeReasonSchema,
    record: RecordRefSchema.nullable(),
  }),
]);
export type Addressee = z.infer<typeof AddresseeSchema>;
